import isEmpty from 'lodash/isEmpty';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import isPostalCode from 'validator/lib/isPostalCode';
import isUrl from 'validator/lib/isURL';

import { TFunction } from '@lyearn/i18n';
import { css } from '@lyearn/style';
import { Fields, FieldType } from '@/components/Form';
import { MultiStepSection } from '@/components/MultiStepForm/types';
import AddCover from '@/modules/events/views/EventEditor/components/AddCover';
import {
  ClientTypeEnum,
  CreateClientInput,
  CreateMediaInput,
  UpdateClientInput,
} from '@/types/schema';

import { CreateClientFormValueType } from './types';

const COVER_IMAGE_STYLES = css`
  width: 12.4rem;
  height: 12.4rem;
  border-radius: 9999rem;
`;
const COVER_IMAGE_SIZE = { width: 124, height: 124 };

export const CREATE_CLIENT_INITIAL_VALUES: CreateClientFormValueType = {
  clientName: '',
  type: ClientTypeEnum.Project,
  clientIcon: undefined,
  primaryContact: {
    name: '',
    phone: undefined,
    mobile: undefined,
  },
  email: undefined,
  website: undefined,
  mailAddress: {
    address: undefined,
    city: undefined,
    country: undefined,
    postalCode: undefined,
    state: undefined,
  },
};

const EMPTY_ARRAY: never[] = [];

export const getCreateClientFormInitialValues = (): CreateClientFormValueType => {
  return CREATE_CLIENT_INITIAL_VALUES;
};

export const validateCreateClientForm = (t: TFunction, values: CreateClientFormValueType): any => {
  const errors: any = {};

  if (isEmpty(values.clientName.trim())) {
    errors.clientName = t('Clients:ClientForm.ClientNameRequired');
  }

  if (isEmpty(values.email?.trim())) {
    errors.email = t('Clients:ClientForm.ClientEmailRequired');
  } else if (!isEmail(values.email!)) {
    errors.email = t('Clients:ClientForm.ClientEmailInvalid');
  }

  if (!isEmpty(values.website) && !isUrl(values.website!)) {
    errors.website = t('Clients:ClientForm.WebsiteInvalid');
  }

  if (isEmpty(values.primaryContact?.name.trim())) {
    errors['primaryContact.name'] = t('Clients:ClientForm.ContactNameRequired');
  }

  if (
    !isEmpty(values.primaryContact?.phone) &&
    !isMobilePhone(values.primaryContact!.phone!, 'any')
  ) {
    errors['primaryContact.phone'] = t('Clients:ClientForm.ContactPhoneInvalid');
  }

  if (
    !isEmpty(values.primaryContact?.mobile) &&
    !isMobilePhone(values.primaryContact!.mobile!, 'any')
  ) {
    errors['primaryContact.mobile'] = t('Clients:ClientForm.ContactMobileInvalid');
  }

  if (
    !isEmpty(values.mailAddress?.postalCode) &&
    !isPostalCode(values.mailAddress!.postalCode!, 'any')
  ) {
    errors['mailAddress.postalCode'] = t('Clients:ClientForm.PostalCodeInvalid');
  }

  return errors;
};

export const getCreateClientFormFields = ({
  t,
  defaultCover,
  isEditing,
}: {
  t: TFunction;
  defaultCover: CreateMediaInput;
  isEditing?: boolean;
}): Fields<CreateClientFormValueType> => ({
  clientName: {
    name: 'clientName',
    type: FieldType.Text,
    label: t('Clients:ClientForm.ClientName'),
    placeholder: t('Clients:ClientForm.ClientNamePlaceholder'),
    required: true,
    props: {
      characterLimit: 50,
    },
  },
  email: {
    name: 'email',
    type: FieldType.Email,
    label: t('Clients:ClientForm.ClientEmail'),
    placeholder: t('Clients:ClientForm.ClientEmailPlaceholder'),
    required: true,
  },
  website: {
    name: 'website',
    type: FieldType.Text,
    label: t('Clients:ClientForm.Website'),
    placeholder: t('Clients:ClientForm.WebsitePlaceholder'),
    props: {
      characterLimit: 100,
    },
  },
  clientIcon: {
    name: 'clientIcon',
    type: FieldType.SlowCustom,
    Component: ({ value, onChange }) => {
      return (
        <AddCover
          cover={value}
          coverImageStyles={COVER_IMAGE_STYLES}
          defaultCover={defaultCover}
          description={t('Clients:ClientForm.ClientImageDescription')}
          disabled={false}
          imageSize={COVER_IMAGE_SIZE}
          overrideError={true}
          title={t('Clients:ClientForm.ClientImage')}
          onChange={onChange}
        />
      );
    },
  },
  'primaryContact.name': {
    name: 'primaryContact.name',
    type: FieldType.Text,
    label: t('Clients:ClientForm.ContactName'),
    placeholder: t('Clients:ClientForm.ContactNamePlaceholder'),
    required: true,
    props: {
      characterLimit: 50,
    },
  },
  'primaryContact.phone': {
    name: 'primaryContact.phone',
    type: FieldType.Text,
    label: t('Clients:ClientForm.ContactPhone'),
    placeholder: t('Clients:ClientForm.ContactPhonePlaceholder'),
  },
  'primaryContact.mobile': {
    name: 'primaryContact.mobile',
    type: FieldType.Text,
    label: t('Clients:ClientForm.ContactMobile'),
    placeholder: t('Clients:ClientForm.ContactMobilePlaceholder'),
  },
  'mailAddress.address': {
    name: 'mailAddress.address',
    type: FieldType.Text,
    label: t('Clients:ClientForm.Address'),
    placeholder: t('Clients:ClientForm.AddressPlaceholder'),
    props: {
      characterLimit: 100,
    },
  },
  'mailAddress.city': {
    name: 'mailAddress.city',
    type: FieldType.Text,
    label: t('Clients:ClientForm.City'),
    placeholder: t('Clients:ClientForm.CityPlaceholder'),
    props: {
      characterLimit: 50,
    },
  },
  'mailAddress.state': {
    name: 'mailAddress.state',
    type: FieldType.Text,
    label: t('Clients:ClientForm.State'),
    placeholder: t('Clients:ClientForm.StatePlaceholder'),
    props: {
      characterLimit: 50,
    },
  },
  'mailAddress.postalCode': {
    name: 'mailAddress.postalCode',
    type: FieldType.Text,
    label: t('Clients:ClientForm.PostalCode'),
    placeholder: t('Clients:ClientForm.PostalCodePlaceholder'),
  },
  'mailAddress.country': {
    name: 'mailAddress.country',
    type: FieldType.Text,
    label: t('Clients:ClientForm.Country'),
    placeholder: t('Clients:ClientForm.CountryPlaceholder'),
    props: {
      characterLimit: 50,
    },
  },
});

const SECTION_CLASSES: MultiStepSection['classes'] = { panel: 'w-full px-32 py-24' };

export const LAYOUT = [
  'clientName',
  'email',
  'website',
  ['primaryContact.name', 'primaryContact.phone'],
  'clientIcon',
];

export function adaptCreateClientInputToFormValue(
  input?: CreateClientInput,
): CreateClientFormValueType {
  return {
    clientName: input?.clientName ?? CREATE_CLIENT_INITIAL_VALUES.clientName,
    type: input?.type ?? CREATE_CLIENT_INITIAL_VALUES.type,
    email: input?.email ?? CREATE_CLIENT_INITIAL_VALUES.email,
    website: input?.website ?? CREATE_CLIENT_INITIAL_VALUES.website,
    mailAddress: input?.mailAddress ?? CREATE_CLIENT_INITIAL_VALUES.mailAddress,
    primaryContact: input?.primaryContact ?? CREATE_CLIENT_INITIAL_VALUES.primaryContact,
    clientIcon: input?.clientIcon ?? CREATE_CLIENT_INITIAL_VALUES.clientIcon,
  };
}

export function adaptFormValuesToCreateClientInput(
  formValues: CreateClientFormValueType,
): CreateClientInput {
  return {
    clientName: formValues.clientName,
    type: formValues.type,
    email: formValues.email,
    website: formValues.website,
    mailAddress: formValues.mailAddress,
    primaryContact: formValues.primaryContact,
    clientIcon: formValues.clientIcon,
  };
}

export function adaptFormValuesToUpdateClientInput(
  formValues: CreateClientFormValueType,
  clientId: string,
): UpdateClientInput {
  return {
    clientId,
    clientName: formValues.clientName,
    email: formValues.email,
    website: formValues.website,
    mailAddress: formValues.mailAddress
      ? {
          address: formValues.mailAddress.address,
          city: formValues.mailAddress.city,
          state: formValues.mailAddress.state,
          postalCode: formValues.mailAddress.postalCode,
          country: formValues.mailAddress.country,
        }
      : undefined,
    primaryContact: formValues.primaryContact
      ? {
          name: formValues.primaryContact.name,
          phone: formValues.primaryContact.phone,
          mobile: formValues.primaryContact.mobile,
        }
      : undefined,
    clientIcon: formValues.clientIcon,
  };
}
